// Path: ./src/app/pages/CreateBannerPage/BannerLivePreview.tsx
import { useState } from "react";
import { Box, Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useTranslation } from "react-i18next";
import { CreateGfBannerDto, GfImage } from "@swagger/typescript-fetch-goflyer";

import { messages } from "../../../locales/messages";
import {
  BANNER_LOCALE_SUFFIXES,
  BannerContentField,
  BannerLocaleSuffix,
  BannerSlotSize,
  bannerContentField,
  getBannerSlotSize,
} from "../../../utils/banner/bannerFields";
import { token } from "../../components/FormKit/styled";

interface Props {
  banner?: CreateGfBannerDto;
  desktopImage?: GfImage;
  mobileImage?: GfImage;
}

type Device = "desktop" | "mobile";

const SLOT_HEIGHT: Record<BannerSlotSize, Record<Device, number>> = {
  masthead: { mobile: 256, desktop: 320 },
  strip: { mobile: 192, desktop: 240 },
};

const skin = {
  card: token.color.surface,
  border: token.color.border,
  foreground: token.color.text,
  mutedForeground: token.color.textMuted,
  primary: token.color.accent,
  primaryForeground: "#ffffff",
  primaryTint: token.color.accentSoft,
  primaryBorder: "#f7c69b",
  muted: token.color.canvas,
} as const;

export const BannerLivePreview = ({
  banner,
  desktopImage,
  mobileImage,
}: Props) => {
  const { t } = useTranslation();
  const [device, setDevice] = useState<Device>("desktop");
  const [suffix, setSuffix] = useState<BannerLocaleSuffix>("");

  const slotSize = getBannerSlotSize(banner?.placement as string | undefined);

  const content = (base: BannerContentField): string => {
    const record = banner as Record<string, unknown> | undefined;
    const localized = record?.[bannerContentField(base, suffix)] as
      string | undefined;
    const english = record?.[base] as string | undefined;
    return (localized || english || "").trim();
  };

  const eyebrow = content("eyebrow");
  const headline = content("headline");
  const titleBadge = content("titleBadge");
  const subtitle = content("subtitle");
  const cta = content("cta");
  const footnote = content("footnote");

  const isMobile = device === "mobile";
  const height = SLOT_HEIGHT[slotSize][device];

  const image = isMobile ? mobileImage || desktopImage : desktopImage;

  const hasContent = Boolean(
    banner?.sponsored ||
    eyebrow ||
    headline ||
    titleBadge ||
    subtitle ||
    cta ||
    footnote,
  );

  const hasLink = Boolean(banner?.linkUrl?.trim());
  const showCta = Boolean(cta) && hasLink;

  const hasSomethingToShow = Boolean(image?.src) || hasContent;

  const localeLabels: Record<BannerLocaleSuffix, string> = {
    "": "EN",
    Chinese: "中文",
    French: "FR",
  };

  const toggleGroupSx = {
    "& .MuiToggleButton-root": {
      textTransform: "none",
      fontSize: 12,
      py: 0.4,
      px: 1.4,
      color: skin.mutedForeground,
      borderColor: skin.border,
      "&.Mui-selected": {
        color: skin.primary,
        backgroundColor: skin.primaryTint,
        "&:hover": { backgroundColor: skin.primaryTint },
      },
    },
  } as const;

  return (
    <>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 2 }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={device}
          onChange={(_e, value: Device | null) => value && setDevice(value)}
          sx={toggleGroupSx}
        >
          <ToggleButton value="desktop" data-testid="banner-preview-desktop">
            {t(messages.banner_preview_desktop())}
          </ToggleButton>
          <ToggleButton value="mobile" data-testid="banner-preview-mobile">
            {t(messages.banner_preview_mobile())}
          </ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={suffix}
          onChange={(_e, value: BannerLocaleSuffix | null) =>
            value !== null && setSuffix(value)
          }
          sx={toggleGroupSx}
        >
          {BANNER_LOCALE_SUFFIXES.map((value) => (
            <ToggleButton key={value || "en"} value={value}>
              {localeLabels[value]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      {!hasSomethingToShow ? (
        <Box
          sx={{
            color: skin.mutedForeground,
            fontSize: 14,
            py: 4,
            textAlign: "center",
          }}
        >
          {t(messages.banner_preview_empty())}
        </Box>
      ) : (
        <Box
          data-testid="banner-live-preview"
          sx={{
            position: "relative",
            overflow: "hidden",
            width: "100%",
            maxWidth: isMobile ? 390 : "100%",
            height,
            borderRadius: "16px",
            border: `1px solid ${skin.border}`,
            backgroundColor: skin.card,
            boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
          }}
        >
          {image?.src ? (
            <Box
              component="img"
              src={image.src}
              alt={image.alt ?? ""}
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          ) : (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                backgroundColor: skin.muted,
              }}
            />
          )}

          {hasContent && (
            <>
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background: `linear-gradient(to right, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 50%, rgba(255,255,255,0) 100%)`,
                }}
              />

              <Box
                sx={{
                  position: "relative",
                  height: "100%",
                  maxWidth: 512,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  gap: "8px",
                  px: isMobile ? "20px" : "32px",
                }}
              >
                {(banner?.sponsored || eyebrow) && (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      width: "fit-content",
                      borderRadius: "999px",
                      backgroundColor: skin.primary,
                      color: skin.primaryForeground,
                      px: "10px",
                      py: "4px",
                      fontSize: 12,
                      lineHeight: "16px",
                      fontWeight: 600,
                    }}
                  >
                    {banner?.sponsored
                      ? t(messages.banner_sponsored())
                      : eyebrow}
                  </Box>
                )}

                {(headline || titleBadge) && (
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "8px" }}
                  >
                    {headline && (
                      <Box
                        component="h2"
                        sx={{
                          m: 0,
                          fontSize: isMobile ? 18 : 30,
                          fontWeight: 700,
                          lineHeight: 1.25,
                          textWrap: "balance",
                          color: skin.foreground,
                        }}
                      >
                        {headline}
                      </Box>
                    )}
                    {titleBadge && (
                      <Box
                        component="span"
                        sx={{
                          flexShrink: 0,
                          borderRadius: "999px",
                          border: `1px solid ${skin.primaryBorder}`,
                          backgroundColor: skin.primaryTint,
                          color: skin.primary,
                          px: "8px",
                          py: "2px",
                          fontSize: 12,
                          lineHeight: "16px",
                          fontWeight: 600,
                        }}
                      >
                        {titleBadge}
                      </Box>
                    )}
                  </Box>
                )}

                {subtitle && (
                  <Box
                    component="p"
                    sx={{
                      m: 0,
                      fontSize: 14,
                      lineHeight: "20px",
                      color: skin.mutedForeground,
                    }}
                  >
                    {subtitle}
                  </Box>
                )}

                {showCta && (
                  <Box
                    sx={{
                      mt: "4px",
                      display: "inline-flex",
                      alignItems: "center",
                      width: "fit-content",
                      borderRadius: "999px",
                      backgroundColor: skin.primary,
                      color: skin.primaryForeground,
                      px: "16px",
                      py: "8px",
                      fontSize: 14,
                      lineHeight: "20px",
                      fontWeight: 600,
                      boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
                    }}
                  >
                    {cta}
                  </Box>
                )}

                {footnote && (
                  <Box
                    component="span"
                    sx={{
                      fontSize: 12,
                      lineHeight: "16px",
                      fontWeight: 500,
                      color: skin.primary,
                    }}
                  >
                    {footnote}
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      )}

      {Boolean(cta) && !hasLink && (
        <Box
          sx={{ mt: 1, fontSize: 12, color: token.color.danger }}
          data-testid="banner-preview-cta-hidden"
        >
          {t(messages.banner_cta_needs_link())}
        </Box>
      )}
    </>
  );
};

export default BannerLivePreview;
